/**
 * Shell - User API Service
 *
 * CRUD-style operations against the gateway's user resource.
 * Mirrors user-management/src/services/userService.js so the patterns stay
 * consistent across the shell and the remote MFEs.
 *
 * Gateway base path: /api/v1/metadata
 */

import { createAPIClient } from './apiClient';
import { createStandardAPIMethods } from './apiHelpers';
import { getGatewayUrl } from '../config/runtimeEndpoints';

/**
 * The gateway host is resolved at login, so the client cannot be built at
 * module-eval — this file is evaluated with the shell bundle, long before any
 * region is known. Built on first call instead, and rebuilt if the gateway ever
 * changes underneath us (a different user logging in on the same device).
 */
let cachedMethods = null;
let cachedBase = null;

const userMethods = () => {
    const gatewayUrl = getGatewayUrl();

    if (!gatewayUrl) {
        throw new Error('userService: no resolved gateway — call after login');
    }

    const base = `${gatewayUrl}/v1/metadata`;

    if (!cachedMethods || cachedBase !== base) {
        cachedBase = base;
        cachedMethods = createStandardAPIMethods(createAPIClient(base, 'users'));
    }

    return cachedMethods;
};

/**
 * Fetch the calling user's own settings (or, for superusers, another user's
 * settings via the optional email param).
 *
 * Gateway: GET /api/v1/metadata/get_user_settings?client_id&correlation_id&source[&email]
 * Stream handler — the actual payload (profile, permissions, notification
 * methods, …) arrives over SSE keyed to `messageId`.
 *
 * @param {string} token
 * @param {string} sseClientId
 * @param {string} messageId - Correlation ID.
 * @param {string} [email]   - Optional admin-on-behalf-of target. Omit for self.
 */
export const getUserSettings = (token, sseClientId, messageId, email) =>
    userMethods().fetch(
        token,
        sseClientId,
        messageId,
        email ? { email } : {},
        'get_user_settings'
    );

/**
 * Update an existing user.
 *
 * Gateway: PUT /api/v1/metadata/edit_user?client_id&correlation_id&source
 * Body: { email, password?, first_name?, last_name?, phone?, timezone?,
 *         language?, roles[]?, assets[]?, features[]?, notification_methods? }
 *
 * `email` is required to identify the user; everything else is a partial update.
 *
 * @param {string} token
 * @param {string} sseClientId
 * @param {string} messageId - Correlation ID; the actual outcome arrives over
 *                             SSE keyed to this value.
 * @param {object} payload
 */
export const editUser = (token, sseClientId, messageId, payload) =>
    userMethods().update(token, sseClientId, messageId, payload, {}, 'edit_user');